import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { staffPaymentApi } from '../../api/staffPaymentApi';
import { formatCurrency, CURRENCY } from '../../config/currency';
import { AppIconButton, EmptyState } from '../ui';

// --- Types ---

export interface StaffPayment {
  id: number;
  type?: 'SALARY' | 'ADVANCE' | 'DEDUCTION' | 'BONUS' | 'OVERTIME';
  categoryName?: string;
  categoryId?: number;
  amount: number;
  paymentMode: string;
  date: string;
  salaryMonth?: string | null;
  salaryFrequency?: 'MONTHLY' | 'WEEKLY' | null;
  salaryWeek?: number | null;
  description?: string | null;
  createdAt: string;
}

const CATEGORY_TO_TYPE: Record<string, StaffPayment['type']> = {
  'salaries & wages': 'SALARY',
  'staff advance': 'ADVANCE',
  'staff bonus': 'BONUS',
  'staff overtime': 'OVERTIME',
  'staff deduction': 'DEDUCTION',
};

const resolveType = (p: StaffPayment): string => {
  if (p.type) return p.type;
  const key = (p.categoryName || '').trim().toLowerCase();
  return CATEGORY_TO_TYPE[key] || '';
};

interface PaymentSummary {
  salary: number;
  advance: number;
  deduction: number;
  bonus: number;
  overtime: number;
  outstandingAdvance: number;
}

export interface StaffPaymentHistoryProps {
  staffId: number;
  staffName?: string;
  dateFrom?: string;
  dateTo?: string;
  onEditPayment?: (payment: StaffPayment) => void;
  refreshKey?: number;
}

// --- Constants ---

const TYPE_STYLES: Record<string, { label: string; color: string }> = {
  SALARY: { label: 'Salary', color: 'var(--text-link)' },
  ADVANCE: { label: 'Advance', color: 'var(--status-warn)' },
  DEDUCTION: { label: 'Deduction', color: '#D99982' },
  BONUS: { label: 'Bonus', color: 'var(--status-ok)' },
  OVERTIME: { label: 'Overtime', color: '#9C27B0' }, /* basil-todo */
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank',
  UPI: 'UPI',
  CARD: 'Card',
  CREDIT: 'Credit',
};

// --- Component ---

const StaffPaymentHistory: React.FC<StaffPaymentHistoryProps> = ({
  staffId,
  staffName,
  dateFrom,
  dateTo,
  onEditPayment,
  refreshKey,
}) => {
  const { theme } = useTheme();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  const [payments, setPayments] = useState<StaffPayment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    salary: 0,
    advance: 0,
    deduction: 0,
    bonus: 0,
    overtime: 0,
    outstandingAdvance: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await staffPaymentApi.getStaffPayments(staffId, params);
      const data = res.data;

      // Handle response: could be { payments, summary } or just array
      if (Array.isArray(data)) {
        setPayments(data);
        // Calculate summary from payments
        const calc: PaymentSummary = { salary: 0, advance: 0, deduction: 0, bonus: 0, overtime: 0, outstandingAdvance: 0 };
        data.forEach((p: StaffPayment) => {
          const t = resolveType(p);
          if (!t) return;
          const key = t.toLowerCase() as keyof Omit<PaymentSummary, 'outstandingAdvance'>;
          if (key in calc) calc[key] += p.amount;
        });
        setSummary(calc);
      } else {
        setPayments(data.payments || data.content || []);
        if (data.summary) {
          setSummary(data.summary);
        }
      }
    } catch (err: unknown) {
      console.error('Error fetching staff payments:', err);
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [staffId, dateFrom, dateTo]);

  useEffect(() => {
    if (staffId) fetchPayments();
  }, [staffId, fetchPayments, refreshKey]);

  // --- Helpers ---

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(CURRENCY.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatSalaryMonth = (ym?: string | null): string => {
    if (!ym || !/^\d{4}-(0[1-9]|1[0-2])$/.test(ym)) return '';
    const [y, m] = ym.split('-');
    const date = new Date(Number(y), Number(m) - 1, 1);
    return date.toLocaleDateString(CURRENCY.locale, { month: 'short', year: 'numeric' });
  };

  const getTypeChip = (type: string) => {
    const style = TYPE_STYLES[type] || { label: type ? type.charAt(0) + type.slice(1).toLowerCase() : 'Payment', color: 'var(--text-tertiary)' };
    return (
      <Chip
        label={style.label}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          backgroundColor: `${style.color}20`,
          color: style.color,
          border: `1px solid ${style.color}44`,
          height: 24,
        }}
      />
    );
  };

  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: '12px',
    backgroundColor: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
  };

  // --- Loading ---

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={80}
              height={56}
              sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', flex: 1 }}
            />
          ))}
        </Box>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={56}
            sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', borderRadius: '10px' }}
          />
        ))}
      </Box>
    );
  }

  // --- Render ---

  return (
    <Box>
      {/* Staff name header */}
      {staffName && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.text, mb: 1.5 }}>
          {staffName} - Payment History
        </Typography>
      )}

      {/* Summary cards */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 2,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: theme.gray?.[300] || '#CCC', borderRadius: 2 },
        }}
      >
        {[
          { label: 'Paid Salary', hint: 'this period', value: summary.salary, color: 'var(--text-link)' },
          { label: 'Advances Given', hint: 'this period', value: summary.advance, color: 'var(--status-warn)' },
          { label: 'Deductions', hint: 'this period', value: summary.deduction, color: theme.danger || '#D99982' },
          { label: 'Unpaid Advance', hint: 'balance owed', value: summary.outstandingAdvance, color: summary.outstandingAdvance > 0 ? (theme.danger || '#D99982') : (theme.success || '#4CAF50') },
        ].map((item) => (
          <Paper
            key={item.label}
            sx={{
              ...cardSx,
              flex: '1 0 auto',
              minWidth: 110,
              p: 1.5,
            }}
            elevation={0}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.gray?.[500] || '#666',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.55rem',
                letterSpacing: '0.05em',
                display: 'block',
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: item.color,
                fontSize: { xs: '0.9rem', sm: '1.05rem' },
                lineHeight: 1.3,
                mt: 0.25,
              }}
            >
              {formatCurrency(item.value, { showDecimals: false })}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.gray?.[400] || '#999',
                fontSize: '0.6rem',
                mt: 0.25,
                display: 'block',
              }}
            >
              {item.hint}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Payment list */}
      {payments.length === 0 ? (
        <EmptyState
          icon="\uD83D\uDCCB"
          title="No payments recorded for this staff member"
          subtitle="Record a salary, advance, or bonus from the Staff Payments page and it will appear here."
        />
      ) : isMobile ? (
        /* Mobile: Card layout */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {payments.map((payment) => (
            <Paper
              key={payment.id}
              sx={{
                ...cardSx,
                p: 2,
              }}
              elevation={0}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                {getTypeChip(resolveType(payment))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1rem' }}>
                    {formatCurrency(payment.amount, { showDecimals: false })}
                  </Typography>
                  {onEditPayment && (
                    <AppIconButton
                      icon={<PencilSquareIcon style={{ width: 14, height: 14 }} />}
                      tooltip="Edit"
                      variant="ghost"
                      size="small"
                      onClick={() => onEditPayment(payment)}
                      sx={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                  {formatDate(payment.date)}
                </Typography>
                {payment.salaryMonth && (
                  <Chip
                    label={`For ${formatSalaryMonth(payment.salaryMonth)}`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.6rem',
                      height: 18,
                      backgroundColor: `${theme.primary}15`,
                      color: theme.primary,
                    }}
                  />
                )}
                <Chip
                  label={PAYMENT_MODE_LABELS[payment.paymentMode] || payment.paymentMode}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.6rem',
                    height: 18,
                    backgroundColor: theme.gray?.[100] || '#F0F0F0',
                    color: theme.gray?.[500] || '#666',
                  }}
                />
                {payment.description && (
                  <Typography variant="caption" noWrap sx={{ color: theme.gray?.[400] || '#999', fontStyle: 'italic', flex: 1, minWidth: 0 }}>
                    {payment.description}
                  </Typography>
                )}
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        /* Desktop: Table layout */
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
          }}
          elevation={0}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'For Month', 'Type', 'Amount', 'Mode', 'Notes', ...(onEditPayment ? [''] : [])].map((header) => (
                  <TableCell
                    key={header || 'actions'}
                    align={header === 'Amount' ? 'right' : 'left'}
                    sx={{
                      fontWeight: 600,
                      color: theme.text,
                      borderBottom: `2px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                      backgroundColor: theme.components?.table?.header || theme.background,
                      fontSize: '0.813rem',
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} sx={{ '&:hover': { backgroundColor: `${theme.primary}08` } }}>
                  <TableCell sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
                    {formatDate(payment.date)}
                  </TableCell>
                  <TableCell sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
                    {formatSalaryMonth(payment.salaryMonth) || (
                      <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontSize: '0.813rem' }}>--</Typography>
                    )}
                  </TableCell>
                  <TableCell>{getTypeChip(resolveType(payment))}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: theme.text, fontSize: '0.95rem' }}>
                    {formatCurrency(payment.amount, { showDecimals: false })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={PAYMENT_MODE_LABELS[payment.paymentMode] || payment.paymentMode}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        backgroundColor: theme.gray?.[100] || '#F0F0F0',
                        color: theme.gray?.[500] || '#666',
                        height: 24,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                    {payment.description || (
                      <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontStyle: 'italic', fontSize: '0.813rem' }}>
                        --
                      </Typography>
                    )}
                  </TableCell>
                  {onEditPayment && (
                    <TableCell align="right" sx={{ width: 48 }}>
                      <AppIconButton
                        icon={<PencilSquareIcon style={{ width: 14, height: 14 }} />}
                        tooltip="Edit"
                        variant="ghost"
                        size="small"
                        onClick={() => onEditPayment(payment)}
                        sx={{ width: 28, height: 28, minWidth: 28, minHeight: 28 }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StaffPaymentHistory;
